/* eslint-disable @typescript-eslint/no-explicit-any */
import { CSSProperties, forwardRef, ReactNode } from 'react';
import { DefaultComponentProps, ObjectField } from '@measured/puck';
import styles from './styles.module.scss';
import getClassNameFactory from '../commonFunction';

const getClassName = getClassNameFactory('Layout', styles);

type LayoutFieldProps = {
  spanCol?: number;
  spanRow?: number;
  grow?: boolean;
};

export type WithLayout<Props extends DefaultComponentProps> = Props & {
  layout?: LayoutFieldProps;
};

type LayoutProps = WithLayout<{
  children: ReactNode;
  className?: string;
  style?: CSSProperties;
}>;

export const layoutField: ObjectField<LayoutFieldProps> = {
  type: 'object',
  objectFields: {
    spanCol: {
      label: 'Grid Columns',
      type: 'number',
      min: 1,
      max: 12,
    },
    spanRow: {
      label: 'Grid Rows',
      type: 'number',
      min: 1,
      max: 12,
    },
    grow: {
      label: 'Flex Grow',
      type: 'radio',
      options: [
        { label: 'true', value: true },
        { label: 'false', value: false },
      ],
    },
  },
};

const Layout = forwardRef<HTMLDivElement, LayoutProps>(
  ({ children, className, layout, style }, ref) => {
    return (
      <div
        className={className}
        style={{
          gridColumn: layout?.spanCol
            ? `span ${Math.max(Math.min(layout.spanCol, 12), 1)}`
            : undefined,
          gridRow: layout?.spanRow
            ? `span ${Math.max(Math.min(layout.spanRow, 12), 1)}`
            : undefined,
          flex: layout?.grow ? '1 1 0' : undefined,
          ...style,
        }}
        ref={ref}>
        {children}
      </div>
    );
  },
);

Layout.displayName = 'Layout';

export { Layout };

export function withLayout(componentConfigOrFn: any) {
  const componentConfig =
    typeof componentConfigOrFn === 'function'
      ? componentConfigOrFn()
      : componentConfigOrFn;

  return {
    ...componentConfig,
    fields: {
      ...componentConfig.fields,
      layout: layoutField,
    },
    defaultProps: {
      ...componentConfig.defaultProps,
      ...componentConfig.defaultProps,
      layout: {
        spanCol: 1,
        spanRow: 1,
        grow: false,
        ...componentConfig.defaultProps?.layout,
      },
    },
    resolveFields: (_: any, params: any) => {
      console.log('params in withLayout', params);

      if (params.parent?.type === 'Grid') {
        return {
          ...componentConfig.fields,
          ...componentConfig.resolveFields?.(_, params),
          layout: {
            ...layoutField,
            objectFields: {
              spanCol: layoutField?.objectFields?.spanCol,
              spanRow: layoutField?.objectFields?.spanRow,
            },
          },
        };
      }
      if (params.parent?.type === 'NewFlex') {
        return {
          ...componentConfig.fields,
          ...componentConfig.resolveFields?.(_, params),
          layout: {
            ...layoutField,
            objectFields: {
              grow: layoutField.objectFields.grow,
            },
          },
        };
      }

      return {
        ...componentConfig.fields,
        ...componentConfig.resolveFields?.(_, params),
      };
    },
    inline: true,
    render: (props: any) => (
      <Layout
        className={getClassName()}
        layout={props?.layout}
        ref={props?.puck?.dragRef}>
        {componentConfig.render(props)}
      </Layout>
    ),
  };
}
